import React, { useState, Fragment } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { CheckoutHeader } from '../components/layout/CheckoutHeader';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { CheckoutFooter } from '../components/layout/CheckoutFooter';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { useScreenInit } from '../useScreenInit.js';
import { CouponModal } from '../components/modals/CouponModal';
import { RewardsVouchersSection } from '../components/checkout/RewardsVouchersSection';
import { LocationModal } from '../components/modals/LocationModal';
import { LoyaltyPointsSection } from '../components/checkout/LoyaltyPointsSection';
import { WalletBalance } from '../components/checkout/WalletBalance';
import { PrescriptionUploadSidebar } from '../components/prescription/PrescriptionUploadSidebar';
import { PaymentProcessingBottomSheet } from '../components/checkout/PaymentProcessingBottomSheet';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPinIcon, EditIcon, TruckIcon, TagIcon, PackageIcon, ZapIcon, SnowflakeIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpIcon, CheckIcon, AlertCircleIcon, HomeIcon, ArrowLeftIcon, XIcon } from 'lucide-react';
export function CheckoutPage() {
  const navigate = useNavigate();
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState('same-day');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const screenInit = useScreenInit();
  const [currentMobileStep, setCurrentMobileStep] = useState<'shipping' | 'payment' | 'complete'>(screenInit.currentMobileStep || 'shipping');
  const [deliveryAddress, setDeliveryAddress] = useState({
    type: 'Home',
    address: 'Q0851+3GR, Al Quds, Riyadh 13214, Saudi Arabia, Riyadh, SA',
    name: 'Ahmed',
    phone: '530151938'
  });
  const [appliedLoyaltyPoints, setAppliedLoyaltyPoints] = useState<{
    program: string;
    amount: number;
  } | null>(null);
  const [walletBalance] = useState(250.0);
  const [appliedWalletBalance, setAppliedWalletBalance] = useState(0);
  const [paymentProcessingOpen, setPaymentProcessingOpen] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [isMobileSummaryExpanded, setIsMobileSummaryExpanded] = useState(false);
  const [showDiscountBreakdown, setShowDiscountBreakdown] = useState(false);
  const [showShippingBreakdown, setShowShippingBreakdown] = useState(false);
  const orderItems = [{
    id: 1,
    name: 'Dyson Corrale Hair Straightener Prussian Blue',
    price: 1999,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1522338242992-e1a54906a8da?w=400&h=400&fit=crop'
  }, {
    id: 2,
    name: 'FREE - Beamarry Argan Smooth Conditioner 380 ML',
    price: 54.12,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1571875257727-256c39da42af?w=400&h=400&fit=crop'
  }];
  const shippingMethods = [{
    id: 'same-day',
    name: 'Same Day Delivery',
    description: 'Get it today',
    price: 0,
    icon: <ZapIcon className="w-5 h-5" data-id="element-6781" data-mp="24nb5mz1laxf2" />,
    badge: 'Get it today',
    details: 'AC Controlled Delivery / Not Chilled'
  }, {
    id: 'standard',
    name: 'Standard Delivery',
    description: '3-5 business days',
    price: 0,
    icon: <PackageIcon className="w-5 h-5" data-id="element-6782" data-mp="3g4awrrc7b67i" />
  }, {
    id: 'express-120',
    name: 'Express Delivery - 120 Minutes',
    description: 'Same day delivery within 2 hours',
    price: 15,
    icon: <ZapIcon className="w-5 h-5" data-id="element-6783" data-mp="aaa1x685f2jf" />
  }, {
    id: 'express-refrigerated',
    name: 'Express Refrigerated Delivery',
    description: 'Temperature controlled delivery',
    price: 0,
    icon: <SnowflakeIcon className="w-5 h-5" data-id="element-6784" data-mp="k8fzxcqu3oj0" />
  }, {
    id: 'express',
    name: 'Express Delivery',
    description: '1-2 business days',
    price: 25,
    icon: <TruckIcon className="w-5 h-5" data-id="element-6785" data-mp="t6hzdnux6946" />
  }];
  const paymentMethods = [{
    id: 'apple',
    name: 'Apple Pay',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/apple-pay.svg?v=2.6.2'
  }, {
    id: 'google',
    name: 'Google Pay',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/google-pay.svg?v=2.6.2'
  }, {
    id: 'mada',
    name: 'Mada',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/mada.svg?v=2.6.2'
  }, {
    id: 'visa',
    name: 'Visa',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/visa.svg?v=2.6.2'
  }, {
    id: 'mastercard',
    name: 'Mastercard',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/master-card.svg?v=2.6.2'
  }, {
    id: 'tamara',
    name: 'Tamara',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/tamara.png?v=2.6.2'
  }, {
    id: 'tabby',
    name: 'Tabby',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/tabby.svg?v=2.6.2'
  }, {
    id: 'stc',
    name: 'STC Pay',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/stc-bank.png?v=2.6.2'
  }, {
    id: 'express',
    name: 'Express Pay',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/express-pay.svg?v=2.6.2'
  }, {
    id: 'emkan',
    name: 'Emkan',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/emkan.png?v=2.6.2'
  }, {
    id: 'madfu',
    name: 'Madfu',
    logo: 'https://cdn.niceonesa.com/web/assets_v2/images/madfu.png?v=2.6.2'
  }, {
    id: 'cash',
    name: 'Cash On Delivery',
    icon: '💵'
  }];
  // Payment method colors mapping
  const paymentMethodColors = {
    apple: 'bg-black hover:bg-gray-900',
    google: 'bg-white text-black border border-gray-300 hover:bg-gray-50',
    mada: 'bg-[#00A8E1] hover:bg-[#0090c5]',
    visa: 'bg-[#1A1F71] hover:bg-[#12154e]',
    mastercard: 'bg-[#EB001B] hover:bg-[#cc0017]',
    tamara: 'bg-[#1a1a1a] hover:bg-black',
    tabby: 'bg-[#3EDDB4] text-black hover:bg-[#32b594]',
    stc: 'bg-[#4F008C] hover:bg-[#3a0066]',
    express: 'bg-[#1E2B55] hover:bg-[#162041]',
    emkan: 'bg-[#00A651] hover:bg-[#008c44]',
    madfu: 'bg-[#5B4FFF] hover:bg-[#4a40cc]',
    cash: 'bg-green-600 hover:bg-green-700'
  };
  const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const discount = 451.86;
  const selectedShipping = shippingMethods.find(m => m.id === selectedDeliveryMethod);
  const shippingCost = selectedShipping?.price || 0;
  const tax = 76.25;
  const loyaltyDiscount = appliedLoyaltyPoints?.amount || 0;
  const walletDiscount = appliedWalletBalance;
  const totalDiscounts = discount + loyaltyDiscount + walletDiscount;
  const total = subtotal - totalDiscounts + shippingCost + tax;
  const installmentOptions = [{
    provider: 'Tamara',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Tamara_Logo.svg/2560px-Tamara_Logo.svg.png',
    installments: 12,
    amount: 133,
    color: 'bg-[#1a1a1a]'
  }, {
    provider: 'Tabby',
    logo: 'https://tabby.ai/assets/images/tabby-logo.svg',
    installments: 4,
    amount: 400,
    color: 'bg-[#3EDDB4]'
  }, {
    provider: 'MADA',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/64/Mada_Logo.svg/2560px-Mada_Logo.svg.png',
    installments: 4,
    amount: 400,
    color: 'bg-[#00A8E1]'
  }, {
    provider: 'Modlu',
    logo: 'https://modlu.com/assets/images/logo.svg',
    installments: 6,
    amount: 267,
    color: 'bg-[#5B4FFF]'
  }];
  const applyCoupon = (code: string) => {
    const normalizedCode = code.trim().toUpperCase();
    if (!normalizedCode) return;
    setCouponCode(normalizedCode);
  };
  const handleLocationSelect = (location: any) => {
    setDeliveryAddress(location);
    setSelectedLocation(location);
    setLocationModalOpen(false);
  };
  const handleShippingMethodChange = (methodId: string) => {
    setSelectedDeliveryMethod(methodId);
    setCurrentMobileStep('payment');
  };
  const handlePaymentMethodSelect = (methodId: string) => {
    setSelectedPaymentMethod(methodId);
    setCurrentMobileStep('complete');
  };
  const handleCheckout = () => {
    if (window.innerWidth < 1024) {
      setPaymentProcessingOpen(true);
    } else {
      console.log('Processing checkout...');
    }
  };
  const handleLoyaltyPointsApplied = (program: string, amount: number) => {
    setAppliedLoyaltyPoints({
      program,
      amount
    });
  };
  const handleWalletBalanceApplied = (amount: number) => {
    setAppliedWalletBalance(amount);
  };
  const handleWalletBalanceRemoved = () => {
    setAppliedWalletBalance(0);
  };
  const getProgramName = (programId: string) => {
    const programs = {
      tamayouz: 'Tamayouz Points',
      qitaf: 'Qitaf Points',
      mokafaa: 'Mokafaa Points'
    };
    return programs[programId] || 'Loyalty Points';
  };
  const getMobileButtonContent = () => {
    if (currentMobileStep === 'shipping') {
      return {
        text: 'Select Delivery Method',
        disabled: false,
        bgColor: 'bg-[#1E2B55]',
        icon: null
      };
    } else if (currentMobileStep === 'payment') {
      return {
        text: 'Select Payment Method',
        disabled: false,
        bgColor: 'bg-[#1E2B55]',
        icon: null
      };
    } else {
      const paymentMethod = paymentMethods.find(m => m.id === selectedPaymentMethod);
      return {
        text: `Pay with ${paymentMethod?.name || 'Selected Method'}`,
        disabled: false,
        bgColor: paymentMethodColors[selectedPaymentMethod] || 'bg-[#1E2B55]',
        icon: paymentMethod?.logo || null
      };
    }
  };
  const handleMobileCheckout = () => {
    if (currentMobileStep === 'shipping') {
      document.getElementById('shipping-section')?.scrollIntoView({
        behavior: 'smooth'
      });
    } else if (currentMobileStep === 'payment') {
      document.getElementById('payment-section')?.scrollIntoView({
        behavior: 'smooth'
      });
    } else {
      handleCheckout();
    }
  };
  const mobileButton = getMobileButtonContent();
  const handlePrescriptionUpload = (file: File) => {
    console.log('Prescription uploaded:', file.name);
  };
  const handleConsultDoctor = () => {
    console.log('Consult doctor clicked');
  };
  const handlePaymentSuccess = () => {
    setPaymentProcessingOpen(false);
    navigate('/thank-you');
  };
  const handlePaymentCancel = () => {
    setPaymentProcessingOpen(false);
  };
  const handlePaymentFailure = (reason: string) => {
    setPaymentProcessingOpen(false);
    setPaymentError(reason);
    setTimeout(() => {
      document.getElementById('payment-section')?.scrollIntoView({
        behavior: 'smooth'
      });
    }, 100);
  };
  return <HelmetProvider data-id="element-6786" data-mp="285qbldaxbrkq">
      <SEO title="Checkout" description="Complete your order at Innova Pharmacy with secure payment options and fast delivery across Saudi Arabia." canonical="/en/checkout/" hreflangAr="/checkout/" hreflangEn="/en/checkout/" hreflangDefault="/checkout/" data-id="element-6787" data-mp="2856qjnpz8n2h" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-6788" data-mp="2mvxlv5qs24ac">
        {/* Simplified Header for Checkout */}
        <div className="hidden md:block" data-id="element-6789" data-mp="2ruqv2lz1794p">
          <CheckoutHeader data-id="element-6790" data-mp="1bdmo85vc69n3" />
        </div>
        <MobileInternalHeader title="Checkout" showActions={false} data-id="element-6791" data-mp="1bgr2jw2imlrh" />

        <main className="flex-grow pt-14 md:pt-16 pb-32 md:pb-0" data-id="element-6792" data-mp="1geiqkf01hnqe">
          <h1 className="sr-only" data-id="element-6793" data-mp="3i3g7paalxedv">Checkout</h1>

          <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8 max-w-7xl" data-id="element-6794" data-mp="3i255hewt8eov">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 sm:mb-8" data-id="element-6795" data-mp="3d4ddym02uul7">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold" data-id="element-6796" data-mp="38balkh71dr6l">
                Checkout
              </h1>

              {/* Desktop Stepper - Better Design */}
              <div className="hidden lg:flex items-center gap-4 min-w-[400px]" data-id="element-6797" data-mp="2zd8995hurc40">
                {[{
                key: 'shipping',
                label: 'Delivery',
                step: 1
              }, {
                key: 'payment',
                label: 'Payment',
                step: 2
              }, {
                key: 'complete',
                label: 'Review',
                step: 3
              }].map((s, idx) => {
                const order = {
                  shipping: 0,
                  payment: 1,
                  complete: 2
                } as const;
                const currentIdx = order[currentMobileStep];
                const isCompleted = idx < currentIdx;
                const isActive = idx === currentIdx;
                return <Fragment key={s.key} data-id="element-6798" data-mp="2fcf73zb5qjfh" data-mpi={idx}>
                      <div className="flex items-center gap-3" data-id="element-6799" data-mp="1ikto6bsdym0u">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300
                          ${isCompleted ? 'bg-emerald-500 text-white' : isActive ? 'bg-[#1E2B55] text-white ring-4 ring-[#1E2B55]/10' : 'bg-gray-100 text-gray-400'}
                        `} data-id="element-6800" data-mp="1imef7rfhrmty">
                          {isCompleted ? <CheckIcon className="w-4 h-4" data-id="element-6801" data-mp="37y6v88rvuo5n" /> : s.step}
                        </div>
                        <span className={`text-sm font-semibold ${isActive ? 'text-[#1E2B55]' : isCompleted ? 'text-emerald-600' : 'text-gray-400'}`} data-id="element-6802" data-mp="31j9fpbdf3x76">
                          {s.label}
                        </span>
                      </div>
                      {idx < 2 && <div className="h-0.5 flex-1 bg-gray-100 rounded-full overflow-hidden" data-id="element-6803" data-mp="3pfds2nafcfm9">
                          <motion.div className="h-full bg-emerald-500" initial={{
                      width: 0
                    }} animate={{
                      width: isCompleted ? '100%' : '0%'
                    }} transition={{
                      duration: 0.5
                    }} data-id="element-6804" data-mp="3kgsqokjfrpw8" />
                        </div>}
                    </Fragment>;
              })}
              </div>
            </div>

            {/* Payment Error Alert - Mobile */}
            {paymentError && <div className="lg:hidden mb-4 bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-6805" data-mp="hpvt701wfdwl">
                <div className="flex items-start gap-3" data-id="element-6806" data-mp="lnl7ggj3x4rf">
                  <AlertCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-6807" data-mp="7xq4q0auvem5" />
                  <div data-id="element-6808" data-mp="cozpamhjrrr6">
                    <h3 className="font-bold text-red-900 mb-1" data-id="element-6809" data-mp="cphtq6ed5ca6">
                      Payment Failed
                    </h3>
                    <p className="text-sm text-red-700" data-id="element-6810" data-mp="3u34uuyx3jsyk">{paymentError}</p>
                    <p className="text-xs text-red-600 mt-2" data-id="element-6811" data-mp="2zogl0ykop3t">
                      Please try another payment method or contact support
                    </p>
                  </div>
                </div>
                <button onClick={() => setPaymentError(null)} className="mt-3 text-sm font-medium text-red-600 hover:text-red-700" data-id="element-6812" data-mp="3q333bil2sfx0">
                  Dismiss
                </button>
              </div>}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-6813" data-mp="1lwt9ef50nipw">
              {/* Main Content Area */}
              <div className="lg:col-span-2 space-y-4 sm:space-y-6" data-id="element-6814" data-mp="1hxam5cpq2vjw">
                {/* Delivery Address Section */}
                <div className="bg-white rounded-lg border border-gray-100 p-3 sm:p-4 md:p-6" data-id="element-6815" data-mp="2elgy6koz4q87">
                  <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4" data-id="element-6816" data-mp="29nymwnnhhyx1">
                    Your Delivery Address
                  </h2>
                  <div className="flex items-start gap-3 sm:gap-4" data-id="element-6817" data-mp="20mqzj0miaihq">
                    <div className="flex-shrink-0" data-id="element-6818" data-mp="20olabm42cu83">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-6819" data-mp="u6oomfdcgto5">
                        <MapPinIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#1E2B55]" data-id="element-6820" data-mp="p5qzfitx2zd5" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-6821" data-mp="35trxx0jx2c3a">
                      <div className="flex items-center justify-between mb-2" data-id="element-6822" data-mp="35ujr64q1jxqe">
                        <h3 className="font-bold text-sm sm:text-base" data-id="element-6823" data-mp="39v3tdmph1rje">
                          {deliveryAddress.type}
                        </h3>
                        <button onClick={() => setLocationModalOpen(true)} className="text-[#1E2B55] hover:text-[#162041] flex-shrink-0" data-id="element-6824" data-mp="3enwve2tk2lrs">
                          <EditIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-6825" data-mp="3tjcs6flisvrv" />
                        </button>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600 mb-1 break-words" data-id="element-6826" data-mp="1ftq4b9uiga1">
                        {deliveryAddress.address}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600" data-id="element-6827" data-mp="236x67g2i8n1z">
                        {deliveryAddress.name}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600" data-id="element-6828" data-mp="1k9my7oohcc2w">
                        {deliveryAddress.phone}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Loyalty Points Section - NEW */}
                <LoyaltyPointsSection onPointsApplied={handleLoyaltyPointsApplied} data-id="element-6829" data-mp="1p1n3zxr8dz7e" />
                {/* Wallet Balance Section - NEW */}
                <WalletBalance availableBalance={walletBalance} onBalanceApplied={handleWalletBalanceApplied} onBalanceRemoved={handleWalletBalanceRemoved} data-id="element-6830" data-mp="11dncuwq0c6sa" />
                {/* Delivery Method Section */}
                <div id="shipping-section" className="bg-white rounded-lg border border-gray-100 p-3 sm:p-4 md:p-6" data-id="element-6831" data-mp="15buk90ykzvqt">
                  <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4" data-id="element-6832" data-mp="2gtcg7e9x7r2s">
                    Please Select The Delivery Method
                  </h2>
                  <div className="space-y-3" data-id="element-6833" data-mp="2pbxuvyqwk5j5">
                    {shippingMethods.map(method => <label key={method.id} className={`flex items-center justify-between p-3 sm:p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedDeliveryMethod === method.id ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-6834" data-mp="2fbdpql09igvn">
                        <div className="flex items-center flex-1" data-id="element-6835" data-mp="3cy4b38ahcrkg">
                          <input type="radio" name="shipping" value={method.id} checked={selectedDeliveryMethod === method.id} onChange={() => handleShippingMethodChange(method.id)} className="mr-3" data-id="element-6836" data-mp="2yaj9jit970en" />
                          <div className="flex items-center gap-2 sm:gap-3 flex-1" data-id="element-6837" data-mp="1r5vp15yqkqvl">
                            <div className={`p-2 rounded-lg ${selectedDeliveryMethod === method.id ? 'bg-[#1E2B55] text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-6838" data-mp="1v5nq6xlsvuqx">
                              {method.icon}
                            </div>
                            <div className="flex-1" data-id="element-6839" data-mp="17glavwen2dzl">
                              <div className="flex items-center gap-2 flex-wrap" data-id="element-6840" data-mp="1cgg1dyui51k4">
                                <p className="font-bold text-xs sm:text-sm" data-id="element-6841" data-mp="1ce39otyxzctv">
                                  {method.name}
                                </p>
                                {method.badge && <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-6842" data-mp="2supnemb9ue7s">
                                    {method.badge}
                                  </span>}
                              </div>
                              <p className="text-xs text-gray-500" data-id="element-6843" data-mp="37vmtqgjfalru">
                                {method.description}
                              </p>
                              {method.details && <p className="text-xs text-gray-400 mt-1" data-id="element-6844" data-mp="3grvtjwbbbmei">
                                  {method.details}
                                </p>}
                            </div>
                          </div>
                        </div>
                        <div className="text-right ml-2 sm:ml-4 flex-shrink-0" data-id="element-6845" data-mp="3lr8e2aepohdx">
                          <p className="font-bold text-sm sm:text-base text-green-600" data-id="element-6846" data-mp="3izf6x568fbg">
                            {method.price === 0 ? 'Free' : `SAR ${method.price.toFixed(2)}`}
                          </p>
                        </div>
                      </label>)}
                  </div>
                </div>
                {/* Payment Method Section */}
                <div id="payment-section" className="bg-white rounded-lg border border-gray-100 p-3 sm:p-4 md:p-6" data-id="element-6847" data-mp="97r73dah0d6r">
                  <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4" data-id="element-6848" data-mp="e5j3okm28nha">
                    Select Payment Method
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4" data-id="element-6849" data-mp="n823swygolzu">
                    {paymentMethods.map(method => <button key={method.id} onClick={() => handlePaymentMethodSelect(method.id)} className={`border-2 rounded-lg p-4 flex sm:flex-col items-center sm:justify-center gap-3 sm:gap-0 hover:border-[#1E2B55] transition-colors min-h-[64px] sm:min-h-[100px] w-full ${selectedPaymentMethod === method.id ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200'}`} data-id="element-6850" data-mp="s4iplki6uo9r">
                        <div className="w-12 sm:w-auto flex justify-center sm:mb-2" data-id="element-6851" data-mp="2vg6tkpdon4dc">
                          {method.logo ? <img src={method.logo} alt={method.name} className="h-6 sm:h-8 object-contain max-w-full" data-id="element-6852" data-mp="2rc8z4rzayhqm" /> : <span className="text-2xl sm:text-3xl" data-id="element-6853" data-mp="3f1tgj6ft56xs">
                              {method.icon}
                            </span>}
                        </div>
                        <span className="text-sm font-medium text-left sm:text-center leading-tight flex-1 sm:flex-none" data-id="element-6854" data-mp="35c0g91l48e8u">
                          {method.name}
                        </span>
                        {selectedPaymentMethod === method.id && <div className="sm:hidden w-5 h-5 rounded-full bg-[#1E2B55] text-white flex items-center justify-center flex-shrink-0" data-id="element-6855" data-mp="3t35w3gr236j5">
                            <CheckIcon className="w-3 h-3" data-id="element-6856" data-mp="3djbdo42k0kdo" />
                          </div>}
                      </button>)}
                  </div>
                  {/* Desktop Checkout Button - Below Payment Methods */}
                  <div className="hidden lg:block mt-6" data-id="element-6857" data-mp="38l02nq0iw3tv">
                    <button onClick={() => {
                    if (currentMobileStep === 'shipping') {
                      document.getElementById('shipping-section')?.scrollIntoView({
                        behavior: 'smooth'
                      });
                    } else if (currentMobileStep === 'payment') {
                      document.getElementById('payment-section')?.scrollIntoView({
                        behavior: 'smooth'
                      });
                    } else {
                      handleCheckout();
                    }
                  }} disabled={currentMobileStep === 'complete' && !selectedPaymentMethod} className={`w-full py-4 rounded-lg font-bold text-white transition-colors flex items-center justify-center gap-3 text-base ${currentMobileStep === 'complete' && !selectedPaymentMethod ? 'bg-gray-300 cursor-not-allowed' : currentMobileStep === 'complete' && selectedPaymentMethod ? paymentMethodColors[selectedPaymentMethod] || 'bg-[#1E2B55]' : 'bg-[#1E2B55] hover:bg-[#162041]'}`} data-id="element-6858" data-mp="38nmlilsv65re">
                      {currentMobileStep === 'shipping' && <>
                          <TruckIcon className="w-5 h-5" data-id="element-6859" data-mp="3a1nlg8dm7m4m" />
                          Select Delivery Method
                        </>}
                      {currentMobileStep === 'payment' && <>
                          <TagIcon className="w-5 h-5" data-id="element-6860" data-mp="27pj09ri0hppu" />
                          Select Payment Method
                        </>}
                      {currentMobileStep === 'complete' && selectedPaymentMethod && <>
                            {paymentMethods.find(m => m.id === selectedPaymentMethod)?.logo ? <img src={paymentMethods.find(m => m.id === selectedPaymentMethod)?.logo} alt="" className="h-6 w-auto" data-id="element-6861" data-mp="1o235hrtjewvt" /> : <span className="text-2xl" data-id="element-6862" data-mp="xrj7jo2s2xi1">
                                {paymentMethods.find(m => m.id === selectedPaymentMethod)?.icon}
                              </span>}
                            Pay with{' '}
                            {paymentMethods.find(m => m.id === selectedPaymentMethod)?.name}
                          </>}
                    </button>
                  </div>
                </div>

                <div className="lg:hidden" data-id="element-6863" data-mp="53pol6fvxty">
                  <RewardsVouchersSection voucherCode={couponCode} orderTotal={total} onVoucherCodeChange={setCouponCode} onApplyVoucher={applyCoupon} onViewAllVouchers={() => setCouponModalOpen(true)} data-id="element-6864" data-mp="3s7lgol13lqu2" />
                </div>
              </div>
              {/* Checkout Sidebar - Desktop */}
              <div className="lg:col-span-1" data-id="element-6865" data-mp="15zdxt2bencfo">
                <div className="space-y-4 lg:sticky lg:top-24" data-id="element-6866" data-mp="15ykopuoz4aud">
                  <div className="hidden lg:block" data-id="element-6867" data-mp="1a0669tcgddaw">
                    <RewardsVouchersSection voucherCode={couponCode} orderTotal={total} onVoucherCodeChange={setCouponCode} onApplyVoucher={applyCoupon} onViewAllVouchers={() => setCouponModalOpen(true)} data-id="element-6868" data-mp="1a2sfdxezs0v8" />
                  </div>
                  {/* Prescription Upload - NEW */}
                  <PrescriptionUploadSidebar onPrescriptionUpload={handlePrescriptionUpload} onConsultDoctor={handleConsultDoctor} data-id="element-6869" data-mp="r7va0icb0dlr" />
                  {/* Order Summary */}
                  <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 md:p-6" data-id="element-6870" data-mp="3usm28roq1az6">
                    {/* Order Totals */}
                    <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6" data-id="element-6871" data-mp="3pvcerhkw79dz">
                      <div className="flex justify-between text-xs sm:text-sm items-center" data-id="element-6872" data-mp="7oxtsyo7hz63">
                        <span className="text-gray-600 flex items-center gap-1" data-id="element-6873" data-mp="3mb7tee7v8gt">
                          Subtotal 
                          <button onClick={() => setShowDiscountBreakdown(true)} className="text-[10px] text-gray-400 hover:text-gray-600 flex items-center" data-id="element-6874" data-mp="hlkb5dhbq83d">View details <ChevronDownIcon className="w-3 h-3 ml-0.5" data-id="element-6875" data-mp="clepqf1ndhtq" /></button>
                        </span>
                        <span className="font-medium" data-id="element-6876" data-mp="re894bk1ii3o">
                          SAR {subtotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs sm:text-sm" data-id="element-6877" data-mp="10dmtqudizyg9">
                        <span className="text-gray-600" data-id="element-6878" data-mp="vf1t4tud3cra">Discount</span>
                        <span className="font-medium text-green-600" data-id="element-6879" data-mp="1xpx3xv55dw2s">
                          -SAR {discount.toFixed(2)}
                        </span>
                      </div>
                      {appliedLoyaltyPoints && <div className="flex justify-between text-xs sm:text-sm" data-id="element-6880" data-mp="1nqpon2eurrd1">
                          <span className="text-gray-600" data-id="element-6881" data-mp="1itpfujqmcai8">
                            {getProgramName(appliedLoyaltyPoints.program)}
                          </span>
                          <span className="font-medium text-green-600" data-id="element-6882" data-mp="1a0m7uqyqspuz">
                            -SAR {appliedLoyaltyPoints.amount.toFixed(2)}
                          </span>
                        </div>}
                      {appliedWalletBalance > 0 && <div className="flex justify-between text-xs sm:text-sm" data-id="element-6883" data-mp="100ll0v1nzrm1">
                          <span className="text-gray-600" data-id="element-6884" data-mp="v2k167wezo9i">Wallet Balance</span>
                          <span className="font-medium text-green-600" data-id="element-6885" data-mp="r31gnd8i9f77">
                            -SAR {appliedWalletBalance.toFixed(2)}
                          </span>
                        </div>}
                      <div className="flex justify-between text-xs sm:text-sm items-center" data-id="element-6886" data-mp="36ovs4jyeh5gb">
                        <span className="text-gray-600 flex items-center gap-1" data-id="element-6887" data-mp="3bql0fru8n50p">
                          Shipping & Handling
                          <button onClick={() => setShowShippingBreakdown(true)} className="text-[10px] text-gray-400 hover:text-gray-600 flex items-center" data-id="element-6888" data-mp="2j14e9bx7okcx">View details <ChevronDownIcon className="w-3 h-3 ml-0.5" data-id="element-6889" data-mp="2o18ud0u7jd2m" /></button>
                        </span>
                        <span className="font-medium text-green-600" data-id="element-6890" data-mp="2syijsghljoqg">
                          {shippingCost === 0 ? 'Free' : `SAR ${shippingCost.toFixed(2)}`}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs sm:text-sm" data-id="element-6891" data-mp="258xvrj6l61ab">
                        <span className="text-gray-600" data-id="element-6892" data-mp="1x8ryl4qrynzb">Tax</span>
                        <span className="font-medium" data-id="element-6893" data-mp="1x6x9de8h09pf">
                          SAR {tax.toFixed(2)}
                        </span>
                      </div>
                      <div className="border-t border-gray-200 pt-2 sm:pt-3 flex justify-between items-center" data-id="element-6894" data-mp="26xrz4skxdrfm">
                        <span className="font-bold text-sm sm:text-base" data-id="element-6895" data-mp="2211lsd0i99xw">
                          Total (Include Vat)
                        </span>
                        <span className="text-lg sm:text-xl font-bold text-[#1E2B55]" data-id="element-6896" data-mp="1a8u213ppdhfa">
                          SAR {total.toFixed(2)}
                        </span>
                      </div>
                      {(appliedLoyaltyPoints || appliedWalletBalance > 0) && <div className="pt-2 border-t border-gray-200" data-id="element-6897" data-mp="1jbbqzpwqso5z">
                          <p className="text-xs text-green-600 font-medium" data-id="element-6898" data-mp="2zlxj3d45ncee">
                            You saved SAR{' '}
                            {(loyaltyDiscount + walletDiscount).toFixed(2)} with
                            points & wallet!
                          </p>
                        </div>}
                    </div>
                    {/* Desktop Complete Purchase Button */}
                    <button onClick={() => {
                    if (currentMobileStep === 'shipping') {
                      document.getElementById('shipping-section')?.scrollIntoView({
                        behavior: 'smooth'
                      });
                    } else if (currentMobileStep === 'payment') {
                      document.getElementById('payment-section')?.scrollIntoView({
                        behavior: 'smooth'
                      });
                    } else {
                      handleCheckout();
                    }
                  }} disabled={currentMobileStep === 'complete' && !selectedPaymentMethod} className={`hidden lg:flex w-full py-2.5 sm:py-3 rounded-lg font-bold text-white transition-colors mb-3 sm:mb-4 text-sm sm:text-base items-center justify-center gap-2 ${currentMobileStep === 'complete' && !selectedPaymentMethod ? 'bg-gray-300 cursor-not-allowed' : currentMobileStep === 'complete' && selectedPaymentMethod ? paymentMethodColors[selectedPaymentMethod] || 'bg-[#1E2B55]' : 'bg-[#1E2B55] hover:bg-[#162041]'}`} data-id="element-6899" data-mp="7i1atan639nc">
                      {currentMobileStep === 'shipping' && <>
                          <TruckIcon className="w-5 h-5 flex-shrink-0" data-id="element-6900" data-mp="45ds5faxkjjw" />
                          <span data-id="element-6901" data-mp="11of9zgm2vm3p">Select Delivery Method</span>
                        </>}
                      {currentMobileStep === 'payment' && <>
                          <TagIcon className="w-5 h-5 flex-shrink-0" data-id="element-6902" data-mp="148v5hgbdxrs2" />
                          <span data-id="element-6903" data-mp="195ll62ja2lcy">Select Payment Method</span>
                        </>}
                      {currentMobileStep === 'complete' && selectedPaymentMethod && <>
                            {paymentMethods.find(m => m.id === selectedPaymentMethod)?.logo ? <img src={paymentMethods.find(m => m.id === selectedPaymentMethod)?.logo} alt="" className="h-6 w-auto flex-shrink-0" data-id="element-6904" data-mp="1s3y4ui7tfdms" /> : <span className="text-2xl flex-shrink-0" data-id="element-6905" data-mp="athe3xa5dhyd">
                                {paymentMethods.find(m => m.id === selectedPaymentMethod)?.icon}
                              </span>}
                            <span data-id="element-6906" data-mp="fqhk686zykph">
                              Pay with{' '}
                              {paymentMethods.find(m => m.id === selectedPaymentMethod)?.name}
                            </span>
                          </>}
                    </button>
                    {/* Free Delivery Banner */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 sm:p-3 mb-3 sm:mb-4 flex items-start gap-2" data-id="element-6907" data-mp="35ab757spckku">
                      <TruckIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-6908" data-mp="30ct6e4ou9bnx" />
                      <p className="text-xs sm:text-sm text-green-700 leading-tight" data-id="element-6909" data-mp="1krhf0nzjbgh9">
                        <strong data-id="element-6910" data-mp="1ktljp859cjil">Congratulations!</strong> You have Unlocked Free
                        Delivery 🎉
                      </p>
                    </div>

                    {/* Order Summary Section - Mobile Only (Moved to bottom) */}
                    <div className="lg:hidden border-t border-gray-200 pt-4 mt-4 mb-4" data-id="element-6911" data-mp="x1n080vnnhud">
                      <h2 className="text-base font-bold mb-3" data-id="element-6912" data-mp="121iyrkll2h2j">
                        Order Summary
                      </h2>
                      
                      <div className="space-y-4" data-id="element-6913" data-mp="o3b45jc0rruj">
                        {/* Shipment 1 */}
                        {orderItems.slice(0, Math.ceil(orderItems.length / 2)).length > 0 && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-6914" data-mp="2ig0wvxqniv0c">
                            <div className="bg-[#4ADE80] px-3 py-2 flex items-center justify-between text-white" data-id="element-6915" data-mp="2mg1ko8zn4ik2">
                              <span className="font-bold text-xs" data-id="element-6916" data-mp="1a9bwemw3c5e8">Shipment 1 of 2</span>
                              <div className="flex items-center gap-1.5" data-id="element-6917" data-mp="15ahh3uxc0yyv">
                                <span className="border border-white/40 rounded px-1.5 py-0.5 text-[9px] font-bold flex items-center gap-1" data-id="element-6918" data-mp="10gd6n1ouy8t1">
                                  <TruckIcon className="w-2.5 h-2.5" data-id="element-6919" data-mp="rgzx690qhk8b" /> Express
                                </span>
                                <span className="text-[10px] font-medium" data-id="element-6920" data-mp="mhdkq1sqc80k">Same day delivery</span>
                              </div>
                            </div>
                            <div className="p-3 space-y-3" data-id="element-6921" data-mp="hkwmufvqafaa">
                              {orderItems.slice(0, Math.ceil(orderItems.length / 2)).map(item => <div key={item.id} className="flex gap-3 pb-3 border-b border-gray-100 last:border-b-0 last:pb-0" data-id="element-6922" data-mp="2bvjsai0le3f2">
                                  <div className="flex-shrink-0 w-14 h-14 bg-gray-50 rounded-lg overflow-hidden" data-id="element-6923" data-mp="2fco97fudzbj2">
                                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-6924" data-mp="2o72m5brg29z0" />
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-6925" data-mp="20hi4qxaxvkru">
                                    <h3 className="text-xs font-medium text-gray-900 mb-1 line-clamp-2" data-id="element-6926" data-mp="20hrtkhk8zlvy">
                                      {item.name}
                                    </h3>
                                    <div className="flex items-center justify-between" data-id="element-6927" data-mp="2agz6j3n2leij">
                                      <span className="text-xs text-gray-600" data-id="element-6928" data-mp="3gv8821u8i50f">
                                        X {item.quantity}
                                      </span>
                                      <span className="text-sm font-bold text-gray-900" data-id="element-6929" data-mp="3j8je5o5wlwda">
                                        SAR {item.price.toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                </div>)}
                            </div>
                          </div>}

                        {/* Shipment 2 */}
                        {orderItems.slice(Math.ceil(orderItems.length / 2)).length > 0 && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-6930" data-mp="esrr4v75m80f">
                            <div className="bg-[#1E2B55] px-3 py-2 flex items-center justify-between text-white" data-id="element-6931" data-mp="jsmg2t7dcniu">
                              <span className="font-bold text-xs" data-id="element-6932" data-mp="osik2sg2geye">Shipment 2 of 2</span>
                              <div className="flex items-center gap-1.5" data-id="element-6933" data-mp="tpr6x6c5t75z">
                                <span className="border border-white/40 rounded px-1.5 py-0.5 text-[9px] font-bold flex items-center gap-1" data-id="element-6934" data-mp="2e8bfakavl0cb">
                                  <TruckIcon className="w-2.5 h-2.5" data-id="element-6935" data-mp="298p38e6s9q53" /> Standard
                                </span>
                                <span className="text-[10px] font-medium" data-id="element-6936" data-mp="24ankjud9rsu5">2-3 business days</span>
                              </div>
                            </div>
                            <div className="p-3 space-y-3" data-id="element-6937" data-mp="2wy1851v2vc1l">
                              {orderItems.slice(Math.ceil(orderItems.length / 2)).map(item => <div key={item.id} className="flex gap-3 pb-3 border-b border-gray-100 last:border-b-0 last:pb-0" data-id="element-6938" data-mp="2n99m1acp7lyl">
                                  <div className="flex-shrink-0 w-14 h-14 bg-gray-50 rounded-lg overflow-hidden" data-id="element-6939" data-mp="3fptz6qql6n6o">
                                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-6940" data-mp="3ay29eif98lhl" />
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-6941" data-mp="zapbaczdj1yc">
                                    <h3 className="text-xs font-medium text-gray-900 mb-1 line-clamp-2" data-id="element-6942" data-mp="14a1u8mn4jovn">
                                      {item.name}
                                    </h3>
                                    <div className="flex items-center justify-between" data-id="element-6943" data-mp="ucx82mta3teu">
                                      <span className="text-xs text-gray-600" data-id="element-6944" data-mp="gcw6141a2fxs">
                                        X {item.quantity}
                                      </span>
                                      <span className="text-sm font-bold text-gray-900" data-id="element-6945" data-mp="ldk6egbec94o">
                                        SAR {item.price.toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                </div>)}
                            </div>
                          </div>}
                      </div>
                    </div>

                    {/* Installment Options */}
                    <div className="space-y-2" data-id="element-6946" data-mp="9185slll5d2l">
                      {installmentOptions.map((option, index) => <div key={index} className="border border-gray-200 rounded-lg p-2 sm:p-3 flex items-center gap-2 sm:gap-3" data-id="element-6947" data-mp="mrl8a8fpjzkj" data-mpi={index}>
                          <div className={`${option.color} rounded-lg p-1.5 sm:p-2 flex items-center justify-center w-10 sm:w-12 h-6 sm:h-8 flex-shrink-0`} data-id="element-6948" data-mp="wqkc9dpj3717">
                            <span className="text-white text-xs font-bold" data-id="element-6949" data-mp="2ykjk1p3fz695">
                              {option.provider}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-6950" data-mp="1km5sjbe79kge">
                            <p className="text-xs sm:text-sm font-medium leading-tight" data-id="element-6951" data-mp="1pkpe3aovsu88">
                              {option.installments} Installments of SAR{' '}
                              {option.amount}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-6952" data-mp="1yldrq4ad4skl">
                              Interest-Free
                            </p>
                          </div>
                          <button className="text-xs font-medium text-[#1E2B55] hover:underline whitespace-nowrap flex-shrink-0 hidden sm:block" data-id="element-6953" data-mp="16vsk77xec3qj">
                            Learn More
                          </button>
                        </div>)}
                    </div>
                    {/* Order Items - Desktop Only */}
                    <div className="hidden lg:block mt-6 pt-6 border-t border-gray-200" data-id="element-6954" data-mp="n709beydp5by">
                      <h3 className="text-base font-bold mb-4" data-id="element-6955" data-mp="n4nlwm9uq2ri">Order Items</h3>
                      
                      <div className="space-y-4" data-id="element-6956" data-mp="aoeff7mh4bnh">
                        {/* Shipment 1 */}
                        {orderItems.slice(0, Math.ceil(orderItems.length / 2)).length > 0 && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-6957" data-mp="6ongggmsmp7v">
                            <div className="bg-[#4ADE80] px-4 py-2.5 flex items-center justify-between text-white" data-id="element-6958" data-mp="1q2iknv0khml">
                              <span className="font-bold text-sm" data-id="element-6959" data-mp="3o3kgu1o48k4h">Shipment 1 of 2</span>
                              <div className="flex items-center gap-2" data-id="element-6960" data-mp="3j47kmqbcu0p8">
                                <span className="border border-white/40 rounded px-2 py-0.5 text-xs font-bold flex items-center gap-1" data-id="element-6961" data-mp="3j4heioziss07">
                                  <TruckIcon className="w-3.5 h-3.5" data-id="element-6962" data-mp="3a39p6wezf1i9" /> Express
                                </span>
                                <span className="text-xs font-medium" data-id="element-6963" data-mp="3546k5bz04vaa">Same day delivery</span>
                              </div>
                            </div>
                            <div className="p-4 space-y-3" data-id="element-6964" data-mp="2vj6voprz2vrz">
                              {orderItems.slice(0, Math.ceil(orderItems.length / 2)).map(item => <div key={item.id} className="flex gap-3 pb-3 border-b border-gray-100 last:border-b-0 last:pb-0" data-id="element-6965" data-mp="2lk7n1777j04z">
                                  <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg overflow-hidden" data-id="element-6966" data-mp="2qjkj8ijyxjk8">
                                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-6967" data-mp="3j65dbqrtw9rh" />
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-6968" data-mp="11201irpkrfz">
                                    <h4 className="text-sm font-medium text-gray-900 mb-1 line-clamp-2" data-id="element-6969" data-mp="ypcmq36lovj">
                                      {item.name}
                                    </h4>
                                    <div className="flex items-center justify-between" data-id="element-6970" data-mp="1qful83h5g1qb">
                                      <span className="text-sm text-gray-600" data-id="element-6971" data-mp="1likqc8azqxv3">
                                        X {item.quantity}
                                      </span>
                                      <span className="text-sm font-bold text-gray-900" data-id="element-6972" data-mp="1v9ytqr62chti">
                                        SAR {item.price.toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                </div>)}
                            </div>
                          </div>}

                        {/* Shipment 2 */}
                        {orderItems.slice(Math.ceil(orderItems.length / 2)).length > 0 && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-6973" data-mp="2ny31rjc3x6zd">
                            <div className="bg-[#1E2B55] px-4 py-2.5 flex items-center justify-between text-white" data-id="element-6974" data-mp="lbpcz5pchsin">
                              <span className="font-bold text-sm" data-id="element-6975" data-mp="qaiowtozhbgp">Shipment 2 of 2</span>
                              <div className="flex items-center gap-2" data-id="element-6976" data-mp="v7smiwmj0tfm">
                                <span className="border border-white/40 rounded px-2 py-0.5 text-xs font-bold flex items-center gap-1" data-id="element-6977" data-mp="1002c2bjqbrhd">
                                  <TruckIcon className="w-3.5 h-3.5" data-id="element-6978" data-mp="140m7naonmn1e" /> Standard
                                </span>
                                <span className="text-xs font-medium" data-id="element-6979" data-mp="18ye1wbcrul8r">2-3 business days</span>
                              </div>
                            </div>
                            <div className="p-4 space-y-3" data-id="element-6980" data-mp="1ixd8zphw28tn">
                              {orderItems.slice(Math.ceil(orderItems.length / 2)).map(item => <div key={item.id} className="flex gap-3 pb-3 border-b border-gray-100 last:border-b-0 last:pb-0" data-id="element-6981" data-mp="3kprqingitb9j">
                                  <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg overflow-hidden" data-id="element-6982" data-mp="21uww29ff76jn">
                                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-6983" data-mp="1x356o28036v3" />
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-6984" data-mp="2ks7trplelrwz">
                                    <h4 className="text-sm font-medium text-gray-900 mb-1 line-clamp-2" data-id="element-6985" data-mp="2gre5gv41ub3k">
                                      {item.name}
                                    </h4>
                                    <div className="flex items-center justify-between" data-id="element-6986" data-mp="26u9ln1xoerpx">
                                      <span className="text-sm text-gray-600" data-id="element-6987" data-mp="10dedc6bg3pqv">
                                        X {item.quantity}
                                      </span>
                                      <span className="text-sm font-bold text-gray-900" data-id="element-6988" data-mp="vgxkwzx7qp7z">
                                        SAR {item.price.toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                </div>)}
                            </div>
                          </div>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <CheckoutFooter data-id="element-6989" data-mp="le4gg31inj7p" />
        {/* Mobile Complete Purchase Button - Sits above the mobile navigation */}
        <div className="lg:hidden fixed bottom-[60px] left-0 right-0 bg-white border-t border-gray-200 p-4 z-40 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]" data-id="element-6990" data-mp="hcsnbnjfofur">
          {/* Redesigned Stepper - Above Button */}
          <div className="flex items-center gap-2 mb-4 px-2" data-id="element-6991" data-mp="2j45rgadkujpr">
            {[{
            key: 'shipping',
            label: 'Delivery'
          }, {
            key: 'payment',
            label: 'Payment'
          }, {
            key: 'complete',
            label: 'Review'
          }].map((s, idx) => {
            const order = {
              shipping: 0,
              payment: 1,
              complete: 2
            } as const;
            const currentIdx = order[currentMobileStep];
            const isCompleted = idx < currentIdx;
            const isActive = idx === currentIdx;
            return <div key={s.key} className="flex-1 flex flex-col gap-1.5" data-id="element-6992" data-mp="1gcldlvzf9ojo" data-mpi={idx}>
                  <div className="flex items-center justify-between px-1" data-id="element-6993" data-mp="1l9l8rbuqh6w0">
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${isActive ? 'text-[#1E2B55]' : isCompleted ? 'text-emerald-600' : 'text-gray-400'}`} data-id="element-6994" data-mp="3apb74clvovqj">
                      {s.label}
                    </span>
                    {isCompleted && <CheckIcon className="w-3 h-3 text-emerald-600" data-id="element-6995" data-mp="397md9prrsqy2" />}
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden relative" data-id="element-6996" data-mp="3489hugmu2bjv">
                    {(isActive || isCompleted) && <motion.div layoutId="step-fill" className={`absolute inset-0 rounded-full ${isCompleted ? 'bg-emerald-500' : 'bg-[#1E2B55]'}`} initial={{
                  x: '-100%'
                }} animate={{
                  x: 0
                }} transition={{
                  duration: 0.5,
                  ease: 'easeOut'
                }} data-id="element-6997" data-mp="2zcaxr58jffp9" />}
                  </div>
                </div>;
          })}
          </div>

          {currentMobileStep === 'complete' ? <div className="flex items-center gap-4" data-id="element-6998" data-mp="r235htaz9bq5">
              <div className="flex-1" data-id="element-6999" data-mp="r2l6124oac3v">
                <p className="text-xs text-gray-500 font-medium" data-id="element-7000" data-mp="w1pefqjrw5t5">
                  Total Amount
                </p>
                <p className="text-lg font-bold text-[#1E2B55]" data-id="element-7001" data-mp="110sk9d7kug26">
                  SAR {total.toFixed(2)}
                </p>
              </div>
              <button onClick={handleCheckout} disabled={!selectedPaymentMethod} className={`flex-1 py-3.5 rounded-lg font-bold text-white transition-colors text-base flex items-center justify-center gap-2 ${!selectedPaymentMethod ? 'bg-gray-300 cursor-not-allowed' : paymentMethodColors[selectedPaymentMethod] || 'bg-[#1E2B55]'}`} data-id="element-7002" data-mp="1501eskafyhys">
                {selectedPaymentMethod && <>
                    {paymentMethods.find(m => m.id === selectedPaymentMethod)?.logo ? <img src={paymentMethods.find(m => m.id === selectedPaymentMethod)?.logo} alt="" className="h-5 w-auto" data-id="element-7003" data-mp="2qf0ahdlqejxn" /> : <span className="text-xl" data-id="element-7004" data-mp="27h76s93aa5z7">
                        {paymentMethods.find(m => m.id === selectedPaymentMethod)?.icon}
                      </span>}
                    Pay Now
                  </>}
                {!selectedPaymentMethod && 'Select Payment'}
              </button>
            </div> : <button onClick={() => {
          if (currentMobileStep === 'shipping') {
            document.getElementById('shipping-section')?.scrollIntoView({
              behavior: 'smooth'
            });
          } else if (currentMobileStep === 'payment') {
            document.getElementById('payment-section')?.scrollIntoView({
              behavior: 'smooth'
            });
          }
        }} className="w-full py-3.5 rounded-lg font-bold text-white transition-colors text-base flex items-center justify-center gap-2 bg-[#1E2B55] hover:bg-[#162041]" data-id="element-7005" data-mp="3myhgremscznx">
              {currentMobileStep === 'shipping' && <>
                  <TruckIcon className="w-5 h-5" data-id="element-7006" data-mp="89z5kwm3wh6m" />
                  Select Delivery Method
                </>}
              {currentMobileStep === 'payment' && <>
                  <TagIcon className="w-5 h-5" data-id="element-7007" data-mp="10ynyw9gx7i7t" />
                  Select Payment Method
                </>}
            </button>}
        </div>
        {/* Modals */}
        <CouponModal isOpen={couponModalOpen} onClose={() => setCouponModalOpen(false)} onApplyCoupon={code => applyCoupon(code)} data-id="element-7008" data-mp="1fl3ft8sgiqij" />
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={handleLocationSelect} initialAddress={deliveryAddress} data-id="element-7009" data-mp="15np23gxx4ftx" />
        {/* Payment Processing Bottom Sheet - Mobile Only */}
        <PaymentProcessingBottomSheet isOpen={paymentProcessingOpen} onClose={() => setPaymentProcessingOpen(false)} paymentMethod={paymentMethods.find(m => m.id === selectedPaymentMethod)?.name || 'Selected Method'} onSuccess={handlePaymentSuccess} onCancel={handlePaymentCancel} onFailure={handlePaymentFailure} data-id="element-7010" data-mp="wp3nfwvkhkyf" />

        {/* Discount Breakdown Bottom Sheet / Modal */}
        <AnimatePresence data-id="element-7011" data-mp="8yhnksvpv6zi">
          {showDiscountBreakdown && <>
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setShowDiscountBreakdown(false)} className="fixed inset-0 bg-black/50 z-[100]" data-id="element-7012" data-mp="2kkjjv41ppsua" />
              <motion.div initial={{
            y: '100%',
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} exit={{
            y: '100%',
            opacity: 0
          }} transition={{
            type: 'spring',
            damping: 25,
            stiffness: 200
          }} className="fixed bottom-0 left-0 right-0 md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:bottom-auto md:right-auto bg-white rounded-t-3xl md:rounded-2xl z-[101] p-5 pb-safe md:pb-5 w-full md:max-w-lg max-h-[90vh] overflow-y-auto" data-id="element-7013" data-mp="2yjrinvtyqyxy">
                <div className="flex items-center justify-between mb-5" data-id="element-7014" data-mp="1i4i0940r09p6">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-7015" data-mp="1n4coezt52l6j">Discount breakdown</h3>
                  <button onClick={() => setShowDiscountBreakdown(false)} className="p-1.5 text-gray-400 hover:text-gray-600" data-id="element-7016" data-mp="gjed4ys1eik6">
                    <XIcon className="w-5 h-5" data-id="element-7017" data-mp="bl33op5nma2h" />
                  </button>
                </div>
                
                <div className="space-y-3" data-id="element-7018" data-mp="pgopknglcdqo">
                  <div className="flex justify-between text-sm text-gray-600 gap-4" data-id="element-7019" data-mp="lh6f6lmebl23">
                    <span data-id="element-7020" data-mp="zgnxqj93m230">Subtotal ({orderItems.length} item) Excluding VAT</span>
                    <span className="flex-shrink-0" data-id="element-7021" data-mp="ui4amfirqk92">{(subtotal + discount).toFixed(2)} SAR</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-900 font-medium gap-4" data-id="element-7022" data-mp="197ikrpu655pi">
                    <span className="uppercase" data-id="element-7023" data-mp="147e5u48wsj1e">ONLINE EXCLUSIVE _ Save 50%</span>
                    <span className="flex-shrink-0" data-id="element-7024" data-mp="1i7oy9bpva5oo">-{discount.toFixed(2)} SAR</span>
                  </div>
                  
                  <div className="border-t border-dashed border-gray-200 pt-3 mt-1" data-id="element-7025" data-mp="1d9dnmyrqzr5e">
                    <div className="flex justify-between text-sm font-bold text-gray-900 mb-2 gap-4" data-id="element-7026" data-mp="1wtw1wi1dc6no">
                      <span data-id="element-7027" data-mp="21u0fo0aw6na7">Total ({orderItems.length} item) Excluding VAT</span>
                      <span className="flex-shrink-0" data-id="element-7028" data-mp="1ntziautu5xzh">{(subtotal - tax).toFixed(2)} SAR</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600 gap-4" data-id="element-7029" data-mp="1rujenw6l4xkk">
                      <span data-id="element-7030" data-mp="1e463lkyed2r0">+item VAT (15%)</span>
                      <span className="flex-shrink-0" data-id="element-7031" data-mp="1e2lc647dpzxd">{tax.toFixed(2)} SAR</span>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-3 mt-1" data-id="element-7032" data-mp="14602v6obu2nq">
                    <div className="flex justify-between font-bold text-gray-900 text-base gap-4" data-id="element-7033" data-mp="143vrk3o3m1df">
                      <span data-id="element-7034" data-mp="192gtc7j00t3z">Total ({orderItems.length} item) Including VAT</span>
                      <span className="flex-shrink-0" data-id="element-7035" data-mp="v5tuu0beduvy">{subtotal.toFixed(2)} SAR</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </>}
        </AnimatePresence>

        {/* Shipping Breakdown Bottom Sheet / Modal */}
        <AnimatePresence data-id="element-7036" data-mp="13bw8rv2f6b7f">
          {showShippingBreakdown && <>
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setShowShippingBreakdown(false)} className="fixed inset-0 bg-black/50 z-[100]" data-id="element-7037" data-mp="pblfkldn0kj3" />
              <motion.div initial={{
            y: '100%',
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} exit={{
            y: '100%',
            opacity: 0
          }} transition={{
            type: 'spring',
            damping: 25,
            stiffness: 200
          }} className="fixed bottom-0 left-0 right-0 md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:bottom-auto md:right-auto bg-white rounded-t-3xl md:rounded-2xl z-[101] p-5 pb-safe md:pb-5 w-full md:max-w-lg max-h-[90vh] overflow-y-auto" data-id="element-7038" data-mp="1m12nl703einy">
                <div className="flex items-center justify-between mb-5" data-id="element-7039" data-mp="2f1sm70zf5ei3">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-7040" data-mp="2b0z08d5je9ru">Shipping breakdown</h3>
                  <button onClick={() => setShowShippingBreakdown(false)} className="p-1.5 text-gray-400 hover:text-gray-600" data-id="element-7041" data-mp="2otolgfd8ranw">
                    <XIcon className="w-5 h-5" data-id="element-7042" data-mp="2juvgxcfxmtzv" />
                  </button>
                </div>
                
                <div className="space-y-6" data-id="element-7043" data-mp="1w513otrpb3f3">
                  {/* Shipment 1 */}
                  <div data-id="element-7044" data-mp="1s4qocqi0r0nk">
                    <div className="inline-flex items-center gap-1 bg-[#4ADE80] text-white px-2 py-0.5 rounded text-[10px] font-bold mb-2" data-id="element-7045" data-mp="1s58r85z6sd4g">
                      <TruckIcon className="w-3 h-3" data-id="element-7046" data-mp="213ktx6dbv1d9" /> Express
                    </div>
                    <div className="flex justify-between text-sm text-gray-900 gap-4 mb-2" data-id="element-7047" data-mp="3hhu9pc3i7hsh">
                      <span data-id="element-7048" data-mp="3cj9g3virbwd0">1x Express delivery item(s)</span>
                      <span className="font-bold flex-shrink-0" data-id="element-7049" data-mp="3cireejdbspxp">0 SAR</span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mb-2" data-id="element-7050" data-mp="3kjfdob2hdboj">
                      <div className="h-full bg-[#84cc16] w-[30%] rounded-full" data-id="element-7051" data-mp="3ojhao6x9tgxh"></div>
                    </div>
                    <p className="text-xs text-gray-600" data-id="element-7052" data-mp="3tiaqi6015k0t">
                      Add <span className="font-bold text-gray-900" data-id="element-7053" data-mp="2cre10q79gq9">SAR 150.00</span> more to get <span className="font-bold text-green-600" data-id="element-7054" data-mp="30w80ggl2qcc1">FREE delivery</span>
                    </p>
                  </div>

                  <div className="border-t border-gray-100" data-id="element-7055" data-mp="35vkk6sgnf3ae"></div>

                  {/* Shipment 2 */}
                  <div data-id="element-7056" data-mp="3asb3donr2f01">
                    <div className="inline-flex items-center gap-1 bg-[#1E2B55] text-white px-2 py-0.5 rounded text-[10px] font-bold mb-2" data-id="element-7057" data-mp="3art0i96l12j5">
                      <TruckIcon className="w-3 h-3" data-id="element-7058" data-mp="2i602se058e38" /> Standard
                    </div>
                    <div className="flex justify-between text-sm text-gray-900 gap-4 mb-2" data-id="element-7059" data-mp="2n3rtj4p1xu5u">
                      <span data-id="element-7060" data-mp="2fkiiz84t7pf7">1x Standard delivery item(s)</span>
                      <span className="font-bold flex-shrink-0" data-id="element-7061" data-mp="2fk0ooh1pjl9x">{shippingCost} SAR</span>
                    </div>
                    <p className="text-xs text-gray-600" data-id="element-7062" data-mp="2ajuwmzrizwrb">
                      Estimated delivery: 2-3 business days
                    </p>
                  </div>
                </div>
              </motion.div>
            </>}
        </AnimatePresence>
      </div>
      <MobileFooterNav data-id="element-7063" data-mp="1wnhgbtyqa1ef" />
    </HelmetProvider>;
}